import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import SaveButton from '@/components/CustomComponent/SaveButton/SaveButton';
import {
  fetchEmployeeByEmployeeId,
  fetchEmployeeDeviceListByEmployeeId,
  checkMACAddressUniqueness,
} from '@/components/organization/Configuration/EmployeeSetting/query-request';
import { COMMON_TEXT } from '@/helpers/common-text';
import './EmployeeInfoDialog.less';
import {
  FOLDER_NAME,
  BUCKET_NAME as IMAGE_BUCKET_NAME,
  MAX_UPLOAD_IMAGE_FILE_SIZE,
  MEMORY_STORE,
} from '@/helpers/constants';
import { Form, Formik, getIn } from 'formik';
import { cloneDeep, isEqual } from 'lodash';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';

import { Image } from 'primereact/image';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
import fileService from '@/services/file-service';

const EMPL_ROW_STATUS = {
  UNCHANGED: 0,
  NEW: 1,
  MODIFIED: 2,
  DELETED: 3,
};

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class EmployeeInfoDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tenantId: props.tenantId,
      selectedEmployee: null,
      isLoadingEmployee: true,
      dialogVisibleCount: 0,
      branchOptions: [],
      employeeImageUrl: null,
    };
    this.formikRef = React.createRef();
    this.employeeChatToolsOptions = [
      { label: 'Teams', value: 'teams' },
      { label: 'なし', value: '' },
    ];
    this.toastRef = React.createRef();
  }

  componentDidMount() {}

  componentDidUpdate(prevProps) {
    const {
      dialogVisibleCount: count,
      selectedEmployee,
      tenantId,
      sessionStore,
    } = this.props;
    const { dialogVisibleCount: prevCount } = prevProps;
    const branches = sessionStore[MEMORY_STORE.BRANCHES] || [];
    const branchOptions = branches
      .filter(branch => +branch.tenant_id === +tenantId)
      .map(branch => {
        return {
          label: branch.branch_name,
          value: branch.branch_id,
        };
      });
    if (count > 0 && count !== prevCount) {
      this.setState({
        tenantId,
        dialogVisibleCount: count,
        selectedEmployee: selectedEmployee,
        branchOptions,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return true;
  }

  getSelectedFloorOptions = ({ branchId }) => {
    const { sessionStore } = this.props;
    const floors = sessionStore[MEMORY_STORE.FLOORS] || [];
    return floors
      .filter(floor => floor.branch_id === branchId)
      .map(floor => {
        return {
          label: floor.floor_name,
          value: floor.floor_id,
        };
      });
  };

  getFormSchema = () => {
    return Yup.object().shape({
      employee: Yup.object().shape({
        employeeName: Yup.string().required(COMMON_TEXT.REQUIRED),
        employeeNumber: Yup.string()
          .test(
            'required-non-blank',
            COMMON_TEXT.REQUIRED,
            value => !!value && value.trim() !== ''
          )
          .required(COMMON_TEXT.REQUIRED),
        employeeDepartment: Yup.string().required(COMMON_TEXT.REQUIRED),
        employeeTeam: Yup.string().required(COMMON_TEXT.REQUIRED),
        employeePcMac: Yup.string()
          .nullable()
          .test(
            'valid-mac-format',
            'MACアドレスの形式が正しくありません',
            (value) => {
              if (!value || value.trim() === '') {
                return true; // Allow empty if optional
              }
              return /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(value.trim());
            }
          ),
        employeeMobileMac: Yup.string()
          .nullable()
          .test(
            'valid-mac-format',
            'MACアドレスの形式が正しくありません',
            (value) => {
              if (!value || value.trim() === '') {
                return true; // Allow empty if optional
              }
              return /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(value.trim());
            }
          ),
      }),
    });
  };

  isFormFieldInvalid = ({ name, touched, errors }) => {
    const error = getIn(errors, name);
    return error ? 'p-invalid' : '';
  };

  getFormErrorMessage = ({ name, touched, errors }) => {
    return this.isFormFieldInvalid({ name, touched, errors }) ? (
      <span className="validation-error-message">{getIn(errors, name)}</span>
    ) : (
      <></>
    );
  };

  fetchEmployeeData = async ({ employeeId, rowIndexId }) => {
    let employeeInfo = await fetchEmployeeByEmployeeId({
      employeeId: employeeId,
    });
    let employeeDevices = await fetchEmployeeDeviceListByEmployeeId({
      employeeId: employeeId,
    });
    let employeePcMac = '';
    let employeeMobileMac = '';
    let employeePcName = '';
    let employeeMobileName = '';
    if (employeeDevices && employeeDevices.length > 0) {
      employeeDevices.forEach(element => {
        const { device_type } = element;
        if (device_type === 'pc') {
          employeePcMac = element.employee_device_mac;
          employeePcName = element.employee_device_name;
        } else if (device_type === 'phone') {
          employeeMobileMac = element.employee_device_mac;
          employeeMobileName = element.employee_device_name;
        }
      });
    }
    employeeInfo = employeeInfo?.[0] ?? {};
    let employee = {
      employeeId: employeeInfo.employee_id,
      employeeName: employeeInfo.employee_name,
      employeeNameReading: employeeInfo.employee_name_reading ?? '',
      employeeNumber: employeeInfo.employee_number,
      employeePhoneNumber: employeeInfo.phone_number,
      employeePhoneNumber2: employeeInfo.phone_number2 ?? '',
      employeeEmail: employeeInfo.email,
      employeeBranchId: employeeInfo.branch_id,
      employeeFloorId: employeeInfo.floor_id,
      employeeChatTools: employeeInfo.chat_tools,
      employeeDepartment: employeeInfo.department,
      employeeTeam: employeeInfo.team,
      employeeVerkadaId: employeeInfo.employee_identifer,
      employeeImage: employeeInfo.employee_image,
      employeePcMac,
      employeeMobileMac,
      employeePcName,
      employeeMobileName,
      rowIndexId,
    };
    employee = {
      ...employee,
      initEmployee: cloneDeep(employee),
    };
    return employee;
  };

  fetchEmployeeImageUrl = async (imageKey) => {
    if (!imageKey) return null;
    
    try {
      const url = await fileService.getFileUrl(imageKey);
      this.setState({ employeeImageUrl: url });
      return url;
    } catch (error) {
      console.error('Failed to fetch employee image URL:', error);
      return null;
    }
  };

  employeeDialogOnShow = async () => {
    const { selectedEmployee } = this.state;
    const { status, employeeId, rowIndexId } = selectedEmployee ?? {};
    let currentEmployee = selectedEmployee;
    
    if (status === EMPL_ROW_STATUS.UNCHANGED) {
      // edit existing employee
      this.setState({
        isLoadingEmployee: true,
      });
      const employee = await this.fetchEmployeeData({ employeeId, rowIndexId });
      this.setState({
        selectedEmployee: employee,
        isLoadingEmployee: false,
      });
      currentEmployee = employee; // Use the newly fetched employee data
    } else {
      // new or editing employee
      this.setState({
        isLoadingEmployee: false,
      });
    }
    if (currentEmployee?.employeeImage) {
      this.fetchEmployeeImageUrl(currentEmployee.employeeImage);
    } else {
      this.setState({ employeeImageUrl: null });
    }
  };

  uploadFileToBucket = async ({ file, fileName }) => {
    const BUCKET_NAME = IMAGE_BUCKET_NAME.STATIC;
    const FILE_PATH = `${FOLDER_NAME.EMPLOYEE_IMAGE}/${fileName}`;
    const FULL_FILE_PATH = `${BUCKET_NAME}/${FILE_PATH}`;
    try {
      // Get presigned upload URL using the new file service API
      const uploadUrl = await fileService.getFileUrl(FULL_FILE_PATH, null, 'PUT');
      
      // Upload the file directly to the presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
      }

      return { success: true, fileUrl: `${FULL_FILE_PATH}` };
    } catch (err) {
      console.error('Upload error:', err);
    }
    return { success: false, fileUrl: null };
  };

  onTemplateUpload = async (event, { employee }) => {
    const files = event.files;
    if (files.length > 1) {
      this.toastRef.current.show({
        severity: 'error',
        summary: COMMON_TEXT.UPLOAD_ERROR_MESSAGE,
        detail: COMMON_TEXT.UPLOAD_MULTIPLE_ERROR_MESSAGE,
      });
      return;
    }
    // Iterate over each file and perform a custom upload operation
    for (let file of files) {
      // Simulate a successful file upload with a delay

      // get extension from file name
      const extension = file.name.split('.').pop();

      // generate new file name
      const newFileName = `${uuidv4()}.${extension}`;
      try {
        if (file.size > MAX_UPLOAD_IMAGE_FILE_SIZE) {
          // For example, check if file size is more than 1MB
          throw new Error(COMMON_TEXT.UPLOAD_EXCEED_MESSAGE);
        }
        const { success: isUploadSuccess, fileUrl } =
          await this.uploadFileToBucket({
            file,
            fileName: newFileName,
          });
        if (!isUploadSuccess) {
          throw new Error(COMMON_TEXT.UPLOAD_FAILED_MESSAGE);
        }
        // set employee image
        employee['employeeImage'] = fileUrl;
        this.setState({
          selectedEmployee: employee,
        });
        
        // Fetch and set the image URL for immediate display
        this.fetchEmployeeImageUrl(fileUrl);

        this.toastRef.current.show({
          severity: 'success',
          summary: COMMON_TEXT.UPLOAD_MESSAGE,
          detail: COMMON_TEXT.UPLOAD_MESSAGE_SUCCESS,
        });
      } catch (error) {
        this.toastRef.current.show({
          severity: 'error',
          summary: COMMON_TEXT.UPLOAD_ERROR_MESSAGE,
          detail: error.message,
        });
      }
    }
  };

  isEmployeeInfoModified = ({ employee, initEmployee }) => {
    const initKeys = Object.keys(initEmployee);
    for (let i = 0; i < initKeys.length; i++) {
      const key = initKeys[i];
      if (!isEqual(employee[key] ?? '', initEmployee[key] ?? '')) {
        return true;
      }
    }
    return false;
  };

  checkMACInUnsavedChanges = (macAddress, deviceType) => {
    const { dataList } = this.props;
    if (!macAddress || macAddress.trim() === '' || !dataList) {
      return null;
    }

    const mac = macAddress.trim().toUpperCase();
    const currentRowIndexId = this.state.selectedEmployee?.rowIndexId;

    // Check all employees in the unsaved changes
    for (const emp of dataList) {
      // Skip the current employee being edited
      if (emp.rowIndexId === currentRowIndexId) {
        continue;
      }

      // Skip deleted employees
      if (emp.status === EMPL_ROW_STATUS.DELETED) {
        continue;
      }

      // Check PC MAC
      if (deviceType === 'pc' && emp.employeePcMac) {
        if (emp.employeePcMac.trim().toUpperCase() === mac) {
          return {
            unique: false,
            employee_name: emp.employeeName || '不明',
            message: `このMACアドレスは既にに存在します（${macAddress}, ${emp.employeeName || '不明'}）`,
          };
        }
      }

      // Check Mobile MAC
      if (deviceType === 'mobile' && emp.employeeMobileMac) {
        if (emp.employeeMobileMac.trim().toUpperCase() === mac) {
          return {
            unique: false,
            employee_name: emp.employeeName || '不明',
            message: `このMACアドレスは既にに存在します（${macAddress}, ${emp.employeeName || '不明'}）`,
          };
        }
      }
    }

    return { unique: true };
  };

  validateMACAddress = async (macAddress, tenantId, excludeEmployeeId, deviceType, fieldName) => {
    if (!macAddress || macAddress.trim() === '') {
      return { valid: true };
    }

    const mac = macAddress.trim();

    // Check both unsaved changes AND database in parallel
    const [unsavedCheck, dbCheck] = await Promise.all([
      Promise.resolve(this.checkMACInUnsavedChanges(mac, deviceType)),
      checkMACAddressUniqueness({
        macAddress: mac,
        tenantId,
        excludeEmployeeId,
      }).catch(error => {
        console.error(`${deviceType} MAC validation error:`, error);
        return { unique: true }; // Allow on API error
      }),
    ]);

    // If either check finds a duplicate, return error
    if (unsavedCheck && !unsavedCheck.unique) {
      return {
        valid: false,
        message: unsavedCheck.message,
      };
    }

    if (dbCheck && !dbCheck.unique) {
      return {
        valid: false,
        message: dbCheck.message || 'このMACアドレスは既に使用されています',
      };
    }

    return { valid: true };
  };

  updateSelectedEmployee = async () => {
    if (this.formikRef.current) {
      try {
        // Set all fields as touched to show error messages
        const formValues = this.formikRef.current.values;
        const touched = {};
        if (formValues.employee) {
          Object.keys(formValues.employee).forEach(key => {
            touched[`employee.${key}`] = true;
          });
        }
        this.formikRef.current.setTouched(touched);
        
        // Clear any existing MAC address errors first
        this.formikRef.current.setFieldError('employee.employeePcMac', undefined);
        this.formikRef.current.setFieldError('employee.employeeMobileMac', undefined);
        
        // Validate MAC address fields - check both unsaved changes and database
        const { employee } = this.formikRef.current.values;
        const { tenantId } = this.state;
        const excludeEmployeeId = employee?.employeeId || null;
        
        // Validate MAC address fields - check both unsaved changes and database
        let hasMacErrors = false;
        const macErrors = {};
        
        // Validate PC MAC if present
        if (employee.employeePcMac && employee.employeePcMac.trim() !== '') {
          const pcMacValidation = await this.validateMACAddress(
            employee.employeePcMac.trim(),
            tenantId,
            excludeEmployeeId,
            'pc',
            'employee.employeePcMac'
          );
          
          if (!pcMacValidation.valid) {
            macErrors['employee.employeePcMac'] = pcMacValidation.message;
            hasMacErrors = true;
          }
        }
        
        // Validate Mobile MAC if present
        if (employee.employeeMobileMac && employee.employeeMobileMac.trim() !== '') {
          const mobileMacValidation = await this.validateMACAddress(
            employee.employeeMobileMac.trim(),
            tenantId,
            excludeEmployeeId,
            'mobile',
            'employee.employeeMobileMac'
          );
          
          if (!mobileMacValidation.valid) {
            macErrors['employee.employeeMobileMac'] = mobileMacValidation.message;
            hasMacErrors = true;
          }
        }
        
        // If MAC validation failed, set errors and return
        if (hasMacErrors) {
          const currentErrors = this.formikRef.current.errors || {};
          this.formikRef.current.setErrors({
            ...currentErrors,
            employee: {
              ...(currentErrors.employee || {}),
              ...Object.keys(macErrors).reduce((acc, key) => {
                const fieldName = key.replace('employee.', '');
                acc[fieldName] = macErrors[key];
                return acc;
              }, {}),
            },
          });
          return;
        }
        
        // Now validate the entire form (for other fields)
        const errors = await this.formikRef.current.validateForm();
        
        // Check if there are any validation errors (including nested employee errors)
        const employeeErrors = errors && errors['employee'] ? errors['employee'] : {};
        const hasEmployeeErrors = employeeErrors && Object.keys(employeeErrors).length > 0;
        const hasOtherErrors = errors && Object.keys(errors).some(key => key !== 'employee' && errors[key]);
        
        if (hasEmployeeErrors || hasOtherErrors) {
          // Show toast for other errors
          const firstError = employeeErrors[Object.keys(employeeErrors)[0]] || 
                           (hasOtherErrors ? 'バリデーションエラーがあります' : '');
          if (firstError) {
            this.toastRef.current.show({
              severity: 'error',
              summary: 'エラー',
              detail: firstError,
              life: 5000,
              closable: true,
            });
          }
          return; // Don't close dialog if there are errors
        }
        
        // All validations passed, proceed with save
        const { initEmployee } = employee;
        if (employee.status === EMPL_ROW_STATUS.NEW) {
          this.props.updateEmployeeInfo({ employee, employeeIndex: -1 });
        } else {
          if (this.isEmployeeInfoModified({ employee, initEmployee })) {
            employee.status = EMPL_ROW_STATUS.MODIFIED;
          } else {
            employee.status = EMPL_ROW_STATUS.UNCHANGED;
          }
          this.props.updateEmployeeInfo({
            employee,
          });
        }
        this.hideDialog();
      } catch (error) {
        console.error('Validation error:', error);
        this.toastRef.current.show({
          severity: 'error',
          summary: 'エラー',
          detail: error.message || 'バリデーションエラーが発生しました',
          life: 5000,
          closable: true,
        });
      }
    }
  };

  employeeDialogFooter = () => {
    const { isLoadingEmployee } = this.state;
    return (
      <div className="dialog-footer-center">
        <SaveButton
          type="button"
          className="figma-save-button"
          loading={isLoadingEmployee}
          disabled={isLoadingEmployee}
          onClick={e => this.updateSelectedEmployee(e)}
          label="保存"
          iconSrc="assets/img/save-setting-icon.svg"
          iconAlt="Save"
        />
      </div>
    );
  };

  hideDialog = () => {
    this.setState({
      selectedEmployee: null,
      isLoadingEmployee: true,
      dialogVisibleCount: 0,
    });
  };

  render() {
    const {
      selectedEmployee,
      isLoadingEmployee,
      branchOptions,
      dialogVisibleCount,
    } = this.state;
    return (
      <div className="employee-info-dialog-wrapper">
        <Toast ref={this.toastRef} position="top-center" />
        <Dialog
          visible={dialogVisibleCount > 0}
          style={{ width: '500px' }}
          breakpoints={{ '960px': '75vw', '641px': '90vw' }}
          header={COMMON_TEXT.EMPLOYEE_INFORMATION}
          modal
          className="p-fluid employee-setting-info-dialog"
          footer={this.employeeDialogFooter}
          onHide={this.hideDialog}
          onShow={this.employeeDialogOnShow}
          appendTo={'self'}
          resizable={false}
        >
          <div className="employee-info-container">
            {selectedEmployee?.employeeId && isLoadingEmployee ? (
              <LoadingSpinner />
            ) : !selectedEmployee ? (
              <>NO_DATA</>
            ) : (
              <Formik
                innerRef={this.formikRef}
                initialValues={{ employee: selectedEmployee }}
                enableReinitialize={true}
                validationSchema={this.getFormSchema()}
                onSubmit={values => {}}
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  touched,
                  errors,
                  resetForm,
                }) => (
                  <Form>
                    <div className="employee-form-layout">
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.NAME}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeName`}
                              value={values.employee.employeeName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeName`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeName`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_NAME_READING}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeNameReading`}
                              value={values.employee.employeeNameReading}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeNameReading`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeNameReading`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_NUMBER}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeNumber`}
                              value={values.employee.employeeNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeNumber`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeNumber`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_PHONE_NUMBER}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeePhoneNumber`}
                              value={values.employee.employeePhoneNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeePhoneNumber`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeePhoneNumber`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_PHONE_NUMBER2}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeePhoneNumber2`}
                              value={values.employee.employeePhoneNumber2}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeePhoneNumber2`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeePhoneNumber2`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">{COMMON_TEXT.EMPLOYEE_EMAIL}</span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeEmail`}
                              value={values.employee.employeeEmail}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeEmail`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeEmail`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_TOOL_CHAT}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="figma-dropdown">
                            <Dropdown
                              id={`employee.employeeChatTools`}
                              value={values.employee.employeeChatTools}
                              options={this.employeeChatToolsOptions}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeChatTools`,
                                touched,
                                errors,
                              })}`}
                            ></Dropdown>
                            {this.getFormErrorMessage({
                              name: `employee.employeeChatTools`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.DEPARTMENT}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeDepartment`}
                              value={values.employee.employeeDepartment}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeDepartment`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeDepartment`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.TEAM}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeTeam`}
                              value={values.employee.employeeTeam}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeTeam`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeTeam`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">{COMMON_TEXT.BRANCH}</span>
                        </div>
                        <div className="field-input">
                          <div className="figma-dropdown">
                            <Dropdown
                              id={`employee.employeeBranchId`}
                              value={values.employee.employeeBranchId}
                              options={branchOptions}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeBranchId`,
                                touched,
                                errors,
                              })}`}
                            ></Dropdown>
                            {this.getFormErrorMessage({
                              name: `employee.employeeBranchId`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">{COMMON_TEXT.FLOOR}</span>
                        </div>
                        <div className="field-input">
                          <div className="figma-dropdown">
                            <Dropdown
                              disabled={!values.employee.employeeBranchId}
                              id={`employee.employeeFloorId`}
                              value={values.employee.employeeFloorId}
                              options={this.getSelectedFloorOptions({
                                branchId: values.employee.employeeBranchId,
                              })}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeFloorId`,
                                touched,
                                errors,
                              })}`}
                            ></Dropdown>
                            {this.getFormErrorMessage({
                              name: `employee.employeeFloorId`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">{COMMON_TEXT.VERKADA_ID}</span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeVerkadaId`}
                              value={values.employee.employeeVerkadaId}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeVerkadaId`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeVerkadaId`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">{COMMON_TEXT.EMPLOYEE_IMAGE}</span>
                        </div>
                                                <div className="field-input">
                          <div className="empl-image-field">
                            <div className="">
                              {values.employee.employeeImage ? (
                                <Image
                                  src={this.state.employeeImageUrl || ''}
                                  preview
                                  alt="No Image"
                                />
                              ) : (
                                <i className="pi pi-eye-slash"></i>
                              )}
                            </div>
                            <div 
                              className="upload-button"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.onchange = (e) => {
                                  const files = e.target.files;
                                  if (files && files.length > 0) {
                                    this.onTemplateUpload({ files }, {
                                      employee: values.employee,
                                    });
                                  }
                                };
                                input.click();
                              }}
                            >
                              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="35" height="35" rx="17.5" fill="#505680" stroke="#505680"/>
                                <path d="M17.0832 24.3337H12.9582C11.5679 24.3337 10.38 23.8524 9.39463 22.8899C8.40921 21.9274 7.9165 20.751 7.9165 19.3607C7.9165 18.1691 8.27553 17.1073 8.99359 16.1753C9.71164 15.2434 10.6512 14.6475 11.8123 14.3878C12.1943 12.9823 12.9582 11.8441 14.104 10.9732C15.2498 10.1024 16.5484 9.66699 17.9998 9.66699C19.7873 9.66699 21.3037 10.2896 22.5488 11.5347C23.7939 12.7798 24.4165 14.2962 24.4165 16.0837C25.4707 16.2059 26.3453 16.6604 27.0405 17.4472C27.7356 18.234 28.0832 19.1545 28.0832 20.2087C28.0832 21.3545 27.6821 22.3285 26.88 23.1305C26.078 23.9326 25.104 24.3337 23.9582 24.3337H18.9165V17.7795L20.3832 19.2003L21.6665 17.917L17.9998 14.2503L14.3332 17.917L15.6165 19.2003L17.0832 17.7795V24.3337Z" fill="white"/>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_DEVICE_PC_MAC}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeePcMac`}
                              value={values.employee.employeePcMac}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeePcMac`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeePcMac`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_DEVICE_PC_NAME}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeePcName`}
                              value={values.employee.employeePcName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_DEVICE_MOBILE_MAC}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeMobileMac`}
                              value={values.employee.employeeMobileMac}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              className={`${this.isFormFieldInvalid({
                                name: `employee.employeeMobileMac`,
                                touched,
                                errors,
                              })}`}
                            />
                            {this.getFormErrorMessage({
                              name: `employee.employeeMobileMac`,
                              touched,
                              errors,
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="field-row">
                        <div className="field-label">
                          <span className="figma-label-style">
                            {COMMON_TEXT.EMPLOYEE_DEVICE_MOBILE_NAME}
                          </span>
                        </div>
                        <div className="field-input">
                          <div className="input-text-field-container">
                            <InputText
                              id={`employee.employeeMobileName`}
                              value={values.employee.employeeMobileName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </Dialog>
      </div>
    );
  }
}

EmployeeInfoDialog.propTypes = {
  selectedEmployee: PropTypes.object,
  dialogVisibleCount: PropTypes.number,
  dataList: PropTypes.array,
  tenantId: PropTypes.number,
};

export default EmployeeInfoDialog;
